import type { CharacterDesign } from '../../shared/characters.ts';
import { gameConfig } from '../../shared/game-config.ts';
import { darknessAt, timeOfDay, type LightSource } from '../../shared/lighting.ts';
import { DIRECTIONS, DIRECTION_VECTORS, type FollowerView, type MapView, type NpcMarker, type Position } from '../../shared/protocol.ts';
import { MAP_MONSTER_CELL_PX } from '../../shared/map-monsters.ts';
import { EMPTY_TILE, hasTrail, hasTrigger, lightSpots, sortRows, motionOf, stepOpening, turnedPoint, withinTrigger, type MapElement, type MapObject } from '../../shared/map-elements.ts';
import { objectPose, type ObjectPose } from '../../shared/object-motion.ts';
import { ObjectGrid } from '../../shared/object-grid.ts';
import { weatherAt } from '../../shared/weather.ts';
import { drawPlacedObject, drawTerrainCell, drawTrailMark, type Scene as ElementScene, type TrailMark } from '../common/element-renderer.ts';
import type { Entity } from './entity.ts';
import { LightLayer, type LitPoint } from './light-layer.ts';
import { TerrainCache } from '../common/terrain-cache.ts';
import { drawIcon } from '../common/icons.ts';
import { drawWeather } from './weather-layer.ts';
import { drawEmote, drawEntity, drawFollower, drawLabel, drawMapMonster, drawMarker, type LabelStyle } from './player-sprite.ts';

/** Part basse du personnage masquée par les terrains qui couvrent (hautes herbes). */
const GRASS_COVER = 0.40;
/**
 * Surfeur, en cases depuis le haut de sa case : le {monstre} est abaissé (`mountDrop`) et coupé à la ligne d'eau
 * (`water`) ; le personnage, assis dessus, est remonté (`seat`) et coupé à la hauteur de son dos (`body`).
 */
const SURF = { mountDrop: 0.1, water: 0.62, seat: 0.40, body: 0.15 } as const;
const BACKGROUND = '#14101e';
const HEART_COLOR = '#ff5d8f';
/** Monstre acteur de scène : son nom monte d'autant (en cases) au-dessus de celui d'un personnage, il est plus grand. */
const MONSTER_LABEL_LIFT = 0.6;
/** Mode économie : durée minimale entre deux images (≈ 30 par seconde). */
const ECONOMY_FRAME_MS = 32;

export interface Scene {
  map: MapView;
  /** La carte et ses éléments, prêts à dessiner. */
  elements: ElementScene;
  self: Entity;
  others: Iterable<Entity>;
  npcs: Iterable<{ entity: Entity; marker: NpcMarker | null }>;
  /** Apparences des personnages, par identifiant. */
  characters: ReadonlyMap<number, CharacterDesign>;
  /** Rochers en train d'être poussés, et où les dessiner à cet instant (ils glissent de leur ancienne case à la nouvelle). */
  pushed?: ReadonlyMap<MapObject, Position>;
  /** État de chaque objet à mécanisme pour le joueur (voir mechanisms.ts) : ses calques à déclenchement le suivent. */
  mechanisms?: ReadonlyMap<MapObject, boolean>;
  /** Vol : le {monstre} qui monte attraper le joueur par en dessous (`arrival` : 0 loin sous lui → 1 sous ses pieds ; `leave` : 0 → 1, il s'éloigne en s'effaçant après l'avoir déposé). */
  lift?: { mount: FollowerView; arrival: number; leave: number };
  /**
   * Voyage en véhicule : l'objet qui emmène le joueur, où il est (`pose`) et depuis combien de ms il est parti (ses calques
   * « au départ » se jouent) ; le joueur est dessiné par-dessus, sans son {monstre} suiveur, sauf s'il est caché dedans
   * (`hidden` : véhicule qui cache le joueur, en route).
   */
  ride?: { object: MapObject; pose: ObjectPose; departed: number; hidden: boolean };
  /** Transition de changement de carte : cercle qui se referme sur le joueur (0 → 1), puis se rouvre. */
  transition?: { closing: boolean; progress: number };
  /** Heure du jour imposée (0 : minuit, 0,5 : midi), pour l'aperçu de l'administration ; sinon celle de l'horloge. */
  timeOfDay?: number;
  /** Combat sur la carte : la caméra se pose sur l'arène et un cercle est tracé au sol (voir BattleField). */
  battle?: BattleScene;
  /**
   * Scène : le personnage qui parle. La caméra glisse pour le centrer dans l'écran au-dessus de `bottom` (en pixels de
   * l'écran : le haut de la boîte de dialogue).
   */
  focus?: { entity: Entity; bottom: number };
}

/**
 * Arène de combat dessinée dans le monde. `amount` (0 → 1) : la caméra glisse du joueur vers `focus` (en cases), placé à la
 * hauteur `screenY` de l'écran (au-dessus du panneau des commandes) ; le cercle au sol apparaît avec lui.
 */
export interface BattleScene {
  amount: number;
  focus: { x: number; y: number };
  screenY: number;
  /** Cercle au sol, en cases : centre et rayons de l'ellipse, couleur du type adverse. */
  ring: { x: number; y: number; rx: number; ry: number; color: string };
  /** Lumières des deux monstres, qui éclairent l'arène la nuit. */
  lights: { x: number; y: number; light: LightSource }[];
  /** Dresseur adverse (PNJ, joueur en duel), dessiné en face du joueur (en cases, coin de sa case), sans son suiveur. */
  stand?: { entity: Entity; x: number; y: number };
}

/** Délai avant qu'une marque de trace apparaisse sous un personnage qui arrive sur sa case. */
const TRAIL_DELAY_MS = 100;

/** Objet posé lumineux : ses lumières, en cases depuis son coin, déjà tournées comme lui. */
interface LitObject {
  object: MapObject;
  element: MapElement;
  light: LightSource;
  spots: { x: number; y: number }[];
}

/**
 * Marque de trace posée sur une case : terrain qui la dessine, passage, instant où elle apparaît, instant d'où part son
 * effacement (son départ de la case, pour le personnage qui s'y tient encore) et durée de l'effacement.
 */
interface PlacedTrail extends TrailMark {
  element: MapElement;
  x: number;
  y: number;
  shownAt: number;
  fadeFrom: number;
  fadeMs: number;
}

/**
 * Brins soulevés par un personnage qui entre dans une case de hautes herbes : quelques pixels de la couleur dominante
 * du terrain, lancés depuis ses pieds, qui retombent en s'effaçant. Durées en ms, vitesses en cases par seconde.
 */
const GRASS_BURST = { delayMs: 70, lifeMs: 420, count: 6, gravity: 9 } as const;

interface GrassBurst {
  world: ElementScene;
  x: number;
  y: number;
  startAt: number;
  blades: { dx: number; vx: number; vy: number; color: string; tall: boolean }[];
}

/** Écart d'ordre de dessin entre deux calques d'objets à ligne égale (sous les suiveurs, à 0.5, et les personnages, à 1). */
const OBJECT_LAYER_ORDER = 0.1;

/**
 * Cadrage d'une scène : temps de réponse de la caméra (ms ; elle couvre les deux tiers du chemin en ce temps), et marges
 * (en cases) gardées autour du personnage qui parle quand le bord de la carte empêche de le centrer : sur les côtés et
 * sous lui, et au-dessus (son nom, sa bulle).
 */
const FRAMING = { ms: 280, side: 2, above: 2.2, below: 1 } as const;

const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

/** Dessine la carte, les joueurs et leurs étiquettes, avec une caméra centrée sur le joueur. */
export class WorldRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private tileSize = 48;
  /** Pas en cours des monstres posés qui se promènent, par identifiant. */
  private readonly monsterSteps = new Map<string, { fromX: number; fromY: number; start: number; ms: number }>();
  /** Zoom choisi dans les options : de -2 (plus de cases à l'écran) à +2 (plus gros), 0 par défaut. */
  private zoom = 0;
  private lastCamera = { x: 0, y: 0 };
  /** Cadrage de scène : décalage de la caméra (pixels de la carte), qui glisse vers sa cible (voir frame). */
  private readonly framing = { x: 0, y: 0, mapId: -1, at: 0 };
  private readonly lights = new LightLayer();
  /** Cases des terrains qui éclairent, par carte prête à dessiner. */
  private readonly terrainLights = new WeakMap<ElementScene, { x: number; y: number; light: LightSource }[]>();
  /** Centres des lumières de chaque objet (voir lightSpots). */
  private readonly objectLights = new WeakMap<MapElement, { x: number; y: number }[]>();
  /** Objets posés lumineux de chaque carte, avec leurs lumières déjà tournées (la liste est remplacée à chaque changement). */
  private readonly litObjects = new WeakMap<readonly MapObject[], { elements: ReadonlyMap<number, MapElement>; lit: LitObject[] }>();
  /** Ouverture des objets à déclenchement (portes), de 0 à 1, et quels éléments ont un calque à déclenchement. */
  private readonly opened = new WeakMap<MapObject, number>();
  /** Instant où le joueur s'est servi de chaque objet (borne de soin…) : ses calques « à l'utilisation » se jouent. */
  private readonly used = new WeakMap<MapObject, number>();
  /** Cases de terrain à déclenchement de chaque carte, et leur ouverture (clé : « x,y,élément »). */
  private readonly terrainTriggers = new WeakMap<ElementScene, { x: number; y: number; element: MapElement; key: string }[]>();
  private readonly openedTerrain = new WeakMap<ElementScene, Map<string, number>>();
  /** Objets posés de chaque carte rangés par morceaux (la liste est remplacée à chaque changement : ramassé, poussé…). */
  private readonly objectGrids = new WeakMap<readonly MapObject[], ObjectGrid>();
  /** Terrain fixe de la carte, dessiné une fois par morceaux. */
  private readonly terrain = new TerrainCache();
  private readonly triggered = new WeakMap<MapElement, boolean>();
  /** Marques de trace de chaque carte, de la plus ancienne à la plus récente (dessinées dans cet ordre). */
  private readonly trails = new WeakMap<ElementScene, PlacedTrail[]>();
  /** Dernière case de chaque personnage, et la marque qu'il y a laissée en arrivant (null : aucune). */
  private readonly lastCells = new WeakMap<Entity, { world: ElementScene; x: number; y: number; mark: PlacedTrail | null }>();
  /** Brins d'herbe en vol, et la dernière case vue de chaque personnage pour savoir quand il entre dans l'herbe. */
  private grassBursts: GrassBurst[] = [];
  private readonly grassCells = new WeakMap<Entity, { world: ElementScene; x: number; y: number }>();
  /** Couleur dominante (et sa version éclaircie) de chaque terrain qui couvre, par identifiant d'élément. */
  private readonly grassColors = new Map<number, [string, string]>();
  private grassSampler: CanvasRenderingContext2D | null = null;
  /** Instant de la dernière image : les portes s'ouvrent et se ferment du temps écoulé depuis. */
  private lastFrame = 0;
  private economy = false;
  private lastPaint = 0;
  private darkness = 0;

  /** Le joueur vient de se servir de l'objet : ses calques « à l'utilisation » se jouent une fois, à partir de maintenant. */
  /** Un monstre posé qui se promène vient de quitter `from` : son pas est animé pendant `ms`. */
  stepMonster(id: string, from: Position, ms: number): void {
    this.monsterSteps.set(id, { fromX: from.x, fromY: from.y, start: this.lastFrame || performance.now(), ms });
  }

  markUsed(object: MapObject): void {
    this.used.set(object, this.lastFrame || performance.now());
  }

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D indisponible.');
    this.ctx = ctx;
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  render(scene: Scene, now: number): void {
    // Mode économie : 30 images par seconde au plus (le canvas garde la dernière image entre deux dessins).
    if (this.economy && now - this.lastPaint < ECONOMY_FRAME_MS) return;
    this.lastPaint = now;
    this.frame(scene, now);
    this.paint(scene, now, innerWidth, innerHeight);
  }

  /** Dessine la vue (`width` × `height` pixels, à la taille habituelle des cases) sur `this.ctx`. */
  private paint(scene: Scene, now: number, width: number, height: number): void {
    const { ctx, tileSize: S } = this;
    const { map, self, elements: world } = scene;
    const base = this.camera(scene, width, height);
    const camera = { x: Math.round(base.x + this.framing.x), y: Math.round(base.y + this.framing.y) };
    this.lastCamera = camera;

    ctx.fillStyle = BACKGROUND;
    ctx.fillRect(0, 0, width, height);

    const x0 = Math.max(0, Math.floor(camera.x / S)), y0 = Math.max(0, Math.floor(camera.y / S));
    const x1 = Math.min(map.width - 1, Math.ceil((camera.x + width) / S)), y1 = Math.min(map.height - 1, Math.ceil((camera.y + height) / S));
    const at = (x: number, y: number) => ({ px: x * S - camera.x, py: y * S - camera.y });
    const npcMarkers = new Map([...scene.npcs].map(({ entity, marker }) => [entity, marker]));
    const characters = [...scene.others, ...npcMarkers.keys(), self];
    const elapsed = this.lastFrame ? now - this.lastFrame : 0;
    this.lastFrame = now;
    // Le décor et les personnages s'arrêtent au bord de la carte : ce qui en déborde (arbre posé au bord, objet mobile
    // qui en sort) est coupé là. La météo, l'obscurité et les noms, dessinés ensuite, ne le sont pas.
    ctx.save();
    ctx.beginPath();
    ctx.rect(Math.round(-camera.x), Math.round(-camera.y), map.width * S, map.height * S);
    ctx.clip();
    // Les calques de terrain d'abord, sous les objets et les personnages.
    const openedTerrain = this.updateTerrainTriggers(world, characters, elapsed);
    this.terrain.draw(ctx, world, x0, y0, x1, y1, at, S, now, (x, y, element) => openedTerrain.get(`${x},${y},${element.id}`) ?? 0);
    // Traces (pas, sillages) : sur le terrain, sous les objets et les personnages.
    this.drawTrails(world, characters, x0, y0, x1, y1, at, S, now);
    if (scene.battle) this.drawBattleRing(scene.battle, camera, now);

    // Objets posés visibles (débordements compris) : leurs calques de sol d'abord, sous tout le monde. Un objet
    // mobile est là où l'heure le place (la même pour tous les joueurs).
    const { overflow } = world;
    const clock = Date.now();
    // Calque par calque : un objet d'un calque du dessus passe devant ceux d'en dessous.
    // Seuls les objets des morceaux de carte autour de l'écran sont regardés (plus ceux qui ont bougé : poussé, véhicule).
    const moved = scene.ride ? [...(scene.pushed?.keys() ?? []), scene.ride.object] : (scene.pushed?.keys() ?? []);
    const visibleObjects = this.objectGrid(map.objects, world).near(x0, y0, x1, y1, moved).flatMap((object) => {
      const element = world.elements.get(object.elementId);
      if (!element) return [];
      const motion = motionOf(object, element);
      const place = scene.pushed?.get(object) ?? object;
      const pose = scene.ride?.object === object ? scene.ride.pose
        : motion ? objectPose(object, motion, clock) : { x: place.x, y: place.y, angle: 0, flipX: false, flipY: false };
      if (pose.x - overflow.left > x1 + 1 || pose.y - overflow.up > y1 + 1
        || pose.x + element.width + overflow.right <= x0 - 1 || pose.y + element.height + overflow.down <= y0 - 1) return [];
      return [{ object, element, pose }];
    });
    const draw = ({ object, pose }: (typeof visibleObjects)[number], pass: 'ground' | 'sorted' | 'front') => {
      const { px, py } = at(pose.x, pose.y);
      const usedAt = this.used.get(object);
      const departed = scene.ride?.object === object ? scene.ride.departed : undefined;
      drawPlacedObject(ctx, world, object, Math.round(px), Math.round(py), S, now, pass, pose, this.opened.get(object), usedAt === undefined ? undefined : now - usedAt, departed);
    };
    for (const { object, element, pose } of visibleObjects) {
      const opened = this.stepTrigger(this.opened.get(object) ?? 0, element, { x: pose.x, y: pose.y, turns: object.turns }, characters, elapsed, scene.mechanisms?.get(object));
      if (opened !== null) this.opened.set(object, opened);
    }
    for (const visible of visibleObjects) draw(visible, 'ground');

    const entities = characters.map((entity) => ({ entity, walk: entity.update(now) }));
    // Le dresseur adverse se tient en face du joueur le temps du combat (sa vraie case ne change pas).
    const stand = scene.battle?.stand;
    if (stand) Object.assign(stand.entity, { renderX: stand.x, renderY: stand.y });
    const styleOf = (entity: Entity): LabelStyle => (entity === self ? 'self' : npcMarkers.has(entity) ? 'npc' : 'player');
    const screen = (entity: Entity) => ({ x: Math.round((entity.renderX + entity.drift) * S - camera.x), y: Math.round((entity.renderY - entity.hop) * S - camera.y) });

    // Personnages et objets par ligne : ce qui est plus bas passe devant. À ligne égale, l'objet est dessiné
    // d'abord pour que le personnage à côté reste visible.
    const layers: { y: number; order: number; draw: () => void }[] = [];
    // Un objet d'un calque du dessus passe devant ceux d'en dessous qu'il chevauche (voir sortRows).
    const rows = sortRows(visibleObjects.map(({ object, element, pose }) => ({ x: pose.x, y: pose.y, layer: object.layer, turns: object.turns, element })));
    visibleObjects.forEach((visible, index) => {
      layers.push({ y: rows[index]!, order: visible.object.layer * OBJECT_LAYER_ORDER, draw: () => draw(visible, 'sorted') });
    });
    // Monstres posés (légendaires…) : triés comme les personnages, ils débordent vers le haut.
    for (const monster of map.monsters) {
      const cells = monster.size / MAP_MONSTER_CELL_PX;
      const reach = Math.ceil(cells / 2) + 1;
      if (monster.x < x0 - reach || monster.x > x1 + reach || monster.y < y0 - 1 || monster.y > y1 + Math.ceil(cells) + 1) continue;
      const step = this.monsterSteps.get(monster.id);
      const progress = step ? Math.min(1, (now - step.start) / step.ms) : 1;
      if (step && progress >= 1) this.monsterSteps.delete(monster.id);
      const x = step ? step.fromX + (monster.x - step.fromX) * progress : monster.x;
      const y = step ? step.fromY + (monster.y - step.fromY) * progress : monster.y;
      const { px, py } = at(x, y);
      layers.push({ y, order: 0.6, draw: () => drawMapMonster(ctx, monster, Math.round(px), Math.round(py), S, cells, monster.dir, progress < 1 ? progress : 0, now) });
    }
    // Monstres suiveurs : triés comme les personnages, juste avant leur dresseur à ligne égale.
    const hearts: { trail: Entity['trail']; progress: number }[] = [];
    for (const { entity, walk } of entities) {
      const { follower, trail } = entity;
      // Le surfeur est assis sur son {monstre} : le suiveur ne marche pas derrière lui ; en voyage, il est à bord.
      // En combat, le suiveur du joueur est entré dans l'arène.
      if (!follower || entity.surf || (entity === self && (scene.ride || scene.battle)) || entity === stand?.entity) continue;
      const petting = entity.petting(now);
      layers.push({
        y: trail.renderY, order: 0.5, draw: () => {
          const px = Math.round(trail.renderX * S - camera.x), py = Math.round(trail.renderY * S - camera.y);
          // Caressé, il fait un petit bond de joie.
          const bounce = petting === null ? 0 : Math.round(Math.sin(Math.min(1, petting * 3) * Math.PI) * S * 0.18);
          drawFollower(ctx, follower, trail.dir, px, py - bounce, S, walk, now, follower.size / MAP_MONSTER_CELL_PX);
          // Dans les hautes herbes, il est caché comme son dresseur.
          this.coverInGrass(world, trail, camera, now);
        }
      });
      if (petting !== null) hearts.push({ trail, progress: petting });
    }
    // Décalage des étiquettes des personnages plus hauts qu'une case, mesuré en les dessinant.
    const labelShift = new Map<Entity, number>();
    // En voyage, le joueur est dessiné juste après le véhicule, à bord.
    const ridden = scene.ride ? visibleObjects.findIndex((visible) => visible.object === scene.ride?.object) : -1;
    for (const { entity, walk } of entities) {
      const aboard = entity === self && ridden >= 0;
      if (entity === self && scene.ride?.hidden) continue;
      layers.push({
        y: aboard ? rows[ridden]! : entity.renderY, order: aboard ? visibleObjects[ridden]!.object.layer * OBJECT_LAYER_ORDER + 0.05 : 1, draw: () => {
          const { x, y } = screen(entity);
          const design = entity.characterId === null ? undefined : scene.characters.get(entity.characterId);
          if (entity.monster) {
            const cells = entity.monster.size / MAP_MONSTER_CELL_PX;
            drawMapMonster(ctx, entity.monster, x, y, S, cells, entity.facing(now), walk, now);
            labelShift.set(entity, -Math.round(S * Math.max(MONSTER_LABEL_LIFT, cells - 0.9)));
            this.coverInGrass(world, entity, camera, now);
            return;
          }
          if (entity.surf) {
            labelShift.set(entity, this.drawSurfer(entity, entity.surf, design, x, y, walk, now));
            return;
          }
          labelShift.set(entity, drawEntity(ctx, entity, design, x, y, S, walk, now));
          this.coverInGrass(world, entity, camera, now);
        }
      });
      // Le {monstre} du Vol passe devant le joueur, les pattes sous lui.
      if (entity === self && scene.lift) {
        const { mount, arrival, leave } = scene.lift;
        const { x, y } = screen(entity);
        layers.push({
          y: entity.renderY, order: 1.1, draw: () => {
            ctx.save();
            ctx.globalAlpha = 1 - leave;
            drawFollower(ctx, mount, entity.dir, x, y + Math.round(S * (0.55 + (1 - arrival) * 2 + leave * 1.5)), S, now / 80, now);
            ctx.restore();
          }
        });
      }
    }
    layers.sort((a, b) => a.y - b.y || a.order - b.order);
    for (const layer of layers) layer.draw();
    this.drawGrassBursts(world, characters, camera, now);
    // Cases « devant » des objets : par-dessus tous les personnages, quelle que soit leur ligne.
    for (const visible of visibleObjects) draw(visible, 'front');
    ctx.restore();
    // La météo, puis l'obscurité, couvrent le décor et les personnages, pas leurs noms ni leurs bulles.
    if (!this.economy) drawWeather(ctx, weatherAt(map.weather, map.id, gameConfig().dayNight, clock), width, height, camera, clock, this.tileSize);
    this.drawLighting(scene, entities.map(({ entity }) => entity), camera, width, height, clock, now);
    // Un PNJ sorti du champ (dans la marge, sur son chemin) n'a plus ni nom ni marqueur.
    const offMap = (entity: Entity) => entity.renderX <= -1 || entity.renderY <= -1 || entity.renderX >= map.width || entity.renderY >= map.height;
    for (const { entity } of entities) {
      if (npcMarkers.has(entity) && offMap(entity)) continue;
      if (entity === self && scene.ride?.hidden) continue;
      const { x, y } = screen(entity);
      const top = y + (labelShift.get(entity) ?? 0);
      drawLabel(ctx, entity, x, top, S, styleOf(entity), entity.bubble(now));
      const marker = npcMarkers.get(entity);
      // Une émotion de scène prend la place du marqueur le temps qu'elle dure.
      const emoting = entity.emoting(now);
      if (emoting) drawEmote(ctx, emoting.emote, x, top, S, emoting.progress);
      else if (marker) drawMarker(ctx, marker, x, top, S, now);
    }
    // Cœurs des suiveurs caressés, par-dessus les noms : ils s'élèvent en grossissant un peu, puis s'effacent.
    for (const { trail, progress } of hearts) {
      ctx.save();
      ctx.globalAlpha = progress < 0.7 ? 1 : 1 - (progress - 0.7) / 0.3;
      const size = S * (0.45 + Math.min(1, progress * 4) * 0.15);
      const sway = Math.sin(progress * Math.PI * 2) * S * 0.08;
      drawIcon(ctx, 'heart', trail.renderX * S - camera.x + S / 2 + sway, trail.renderY * S - camera.y - S * (0.45 + progress * 0.6), size, HEART_COLOR);
      ctx.restore();
    }
    if (scene.transition) this.drawIris(scene.transition, screen(self), width, height);
  }

  /**
   * Surfeur : le personnage assis, immobile (pas d'animation de marche), le bas coupé, et devant lui son {monstre} à
   * moitié immergé (coupé à la ligne d'eau, avec un remous). Renvoie le décalage de son étiquette.
   */
  private drawSurfer(entity: Entity, mount: FollowerView, design: CharacterDesign | undefined, x: number, y: number, walk: number, now: number): number {
    const { ctx, tileSize: S } = this;
    const at = (cells: number) => y + Math.round(S * cells);
    const clipAbove = (bottom: number) => {
      ctx.save();
      ctx.beginPath();
      ctx.rect(x - S, y - 3 * S, 3 * S, bottom - (y - 3 * S));
      ctx.clip();
    };
    clipAbove(at(SURF.body));
    const shift = drawEntity(ctx, entity, design, x, at(-SURF.seat), S, 0, now);
    ctx.restore();
    clipAbove(at(SURF.water));
    drawFollower(ctx, mount, entity.dir, x, at(SURF.mountDrop), S, walk, now);
    ctx.restore();
    // Remous à la ligne d'eau, qui ondule doucement.
    ctx.fillStyle = 'rgba(235, 248, 255, 0.55)';
    ctx.beginPath();
    ctx.ellipse(x + S / 2, at(SURF.water), S * (0.42 + 0.03 * Math.sin(now / 300)), S * 0.07, 0, 0, Math.PI * 2);
    ctx.fill();
    return shift - Math.round(S * SURF.seat);
  }

  /**
   * Objet ou case de terrain à déclenchement (porte) : il s'ouvre tant qu'un personnage (joueur ou PNJ) se tient à au
   * plus `triggerRadius` cases de l'une de ses cases de déclenchement (réglées sur l'élément), diagonales comprises, et se referme en sens inverse
   * sinon, d'où qu'il en soit (voir stepOpening). Renvoie sa nouvelle ouverture, ou null s'il n'a pas de calque à
   * déclenchement.
   */
  private stepTrigger(opened: number, element: MapElement, at: Pick<MapObject, 'x' | 'y' | 'turns'>, characters: readonly Entity[], elapsed: number,
    active?: boolean): number | null {
    let triggered = this.triggered.get(element);
    if (triggered === undefined) this.triggered.set(element, triggered = hasTrigger(element));
    if (!triggered) return null;
    // Objet à mécanisme : il suit son état, pas les personnages proches.
    const open = active ?? characters.some((character) => withinTrigger(at, element, character));
    return stepOpening(opened, element, open, elapsed);
  }

  private objectGrid(objects: readonly MapObject[], world: ElementScene): ObjectGrid {
    let grid = this.objectGrids.get(objects);
    if (grid?.elements !== world.elements) this.objectGrids.set(objects, grid = new ObjectGrid(objects, world.elements, world.overflow));
    return grid;
  }

  /** Fait avancer les cases de terrain à déclenchement de la carte ; renvoie leur ouverture. */
  private updateTerrainTriggers(world: ElementScene, characters: readonly Entity[], elapsed: number): Map<string, number> {
    let cells = this.terrainTriggers.get(world);
    if (!cells) {
      const found: { x: number; y: number; element: MapElement; key: string }[] = [];
      for (const layer of [world.tiles, ...world.upperTiles]) {
        layer.forEach((id, i) => {
          const element = world.elements.get(id);
          if (!element || !hasTrigger(element)) return;
          const x = i % world.width, y = Math.floor(i / world.width);
          found.push({ x, y, element, key: `${x},${y},${element.id}` });
        });
      }
      this.terrainTriggers.set(world, cells = found);
    }
    let opened = this.openedTerrain.get(world);
    if (!opened) this.openedTerrain.set(world, opened = new Map());
    for (const { x, y, element, key } of cells) {
      const next = this.stepTrigger(opened.get(key) ?? 0, element, { x, y, turns: 0 }, characters, elapsed);
      if (next !== null) opened.set(key, next);
    }
    return opened;
  }

  /**
   * Traces : chaque personnage qui arrive sur une case y laisse une marque (après TRAIL_DELAY_MS), si son terrain a un
   * calque « Trace » ; en repartant, il la complète du sens de départ (ligne droite ou virage) et elle commence à
   * s'effacer, en la durée de ce calque. Les marques s'empilent : des allers-retours sur une case gardent les plus
   * anciennes, qui finissent de s'effacer. Un saut ou un téléport ne laisse rien.
   */
  private drawTrails(world: ElementScene, characters: readonly Entity[], x0: number, y0: number, x1: number, y1: number,
    at: (x: number, y: number) => { px: number; py: number }, S: number, now: number): void {
    let marks = this.trails.get(world);
    if (!marks) this.trails.set(world, marks = []);
    for (const character of characters) {
      const last = this.lastCells.get(character);
      const { x, y } = character;
      if (last?.world === world && last.x === x && last.y === y) continue;
      const dir = last?.world === world ? DIRECTIONS.find((d) => DIRECTION_VECTORS[d][0] === x - last.x && DIRECTION_VECTORS[d][1] === y - last.y) : undefined;
      let mark: PlacedTrail | null = null;
      if (last && dir) {
        if (last.mark) Object.assign(last.mark, { exit: dir, fadeFrom: Math.max(now, last.mark.shownAt) });
        else this.leaveTrail(world, marks, last.x, last.y, { entry: null, exit: dir }, now);
        mark = this.leaveTrail(world, marks, x, y, { entry: dir, exit: null }, now);
      }
      this.lastCells.set(character, { world, x, y, mark });
    }
    const { ctx } = this;
    let kept = 0;
    for (const mark of marks) {
      const age = now - mark.fadeFrom;
      if (age >= mark.fadeMs) continue;
      marks[kept++] = mark;
      if (now < mark.shownAt || mark.x < x0 - 1 || mark.x > x1 + 1 || mark.y < y0 - 1 || mark.y > y1 + 1) continue;
      const { px, py } = at(mark.x, mark.y);
      ctx.save();
      ctx.globalAlpha = Math.min(1, 1 - age / mark.fadeMs);
      drawTrailMark(ctx, world, mark.element, mark.x, mark.y, px, py, S, now, mark);
      ctx.restore();
    }
    marks.length = kept;
  }

  /**
   * Ajoute une marque sur la case, si son terrain le plus haut a une trace (un terrain posé par-dessus, comme un pont
   * sur l'eau, n'en laisse donc pas) ; renvoie la marque (null : aucune).
   */
  private leaveTrail(world: ElementScene, marks: PlacedTrail[], x: number, y: number, trail: TrailMark, now: number): PlacedTrail | null {
    if (x < 0 || y < 0 || x >= world.width || y >= world.height) return null;
    const index = y * world.width + x;
    const element = [...world.upperTiles].reverse().concat([world.tiles])
      .map((layer) => world.elements.get(layer[index] ?? EMPTY_TILE))
      .find((candidate) => candidate !== undefined);
    if (!element || !hasTrail(element)) return null;
    const fadeMs = Math.max(...element.layers.map((layer) => (layer.shape.mode === 'trail' ? layer.shape.fadeMs : 0)));
    const shownAt = now + TRAIL_DELAY_MS;
    const mark = { ...trail, element, x, y, shownAt, fadeFrom: shownAt, fadeMs };
    marks.push(mark);
    return mark;
  }

  /** Obscurité de la carte (permanente, ou de la nuit), percée par les éléments qui éclairent et les lampes des joueurs. */
  private drawLighting({ map, elements: world, timeOfDay: time, battle }: Scene, entities: readonly Entity[], camera: { x: number; y: number }, width: number, height: number, clock: number, now: number): void {
    const { dayNight } = gameConfig();
    const ambient = darknessAt(map, dayNight, time ?? timeOfDay(dayNight, clock));
    this.darkness = ambient.darkness;
    if (ambient.darkness <= 0.005) return;
    const S = this.tileSize;
    const points: LitPoint[] = [];
    const add = (x: number, y: number, light: LightSource, seed: number) => points.push({ x: x * S - camera.x, y: y * S - camera.y, radius: light.radius * S, light, seed });
    for (const { x, y, light } of this.terrainLightsOf(world)) add(x + 0.5, y + 0.5, light, x * 12.9898 + y * 78.233);
    // Seuls les objets lumineux, relevés une fois par carte (des milliers d'objets sur les grandes routes).
    for (const { object, element, light, spots } of this.litObjectsOf(map.objects, world)) {
      const motion = motionOf(object, element);
      const pose = motion ? objectPose(object, motion, clock) : object;
      for (const spot of spots) add(pose.x + spot.x, pose.y + spot.y, light, (object.x + spot.x) * 12.9898 + (object.y + spot.y) * 78.233);
    }
    for (const entity of entities) {
      const light = entity.light ?? entity.glow;
      if (light) add(entity.renderX + 0.5, entity.renderY + 0.5 - entity.hop, light, entity.id);
    }
    for (const [index, { x, y, light }] of (battle?.lights ?? []).entries()) add(x, y, light, index * 31.7);
    this.lights.draw(this.ctx, width, height, ambient, points, now);
  }

  private litObjectsOf(objects: readonly MapObject[], world: ElementScene): LitObject[] {
    const known = this.litObjects.get(objects);
    if (known?.elements === world.elements) return known.lit;
    const lit = objects.flatMap((object): LitObject[] => {
      const element = world.elements.get(object.elementId);
      if (!element?.light) return [];
      let spots = this.objectLights.get(element);
      if (!spots) this.objectLights.set(element, spots = lightSpots(element));
      return [{ object, element, light: element.light, spots: spots.map((point) => turnedPoint(object.turns, element, point)) }];
    });
    this.litObjects.set(objects, { elements: world.elements, lit });
    return lit;
  }

  private terrainLightsOf(world: ElementScene): { x: number; y: number; light: LightSource }[] {
    let lights = this.terrainLights.get(world);
    if (!lights) {
      const found: { x: number; y: number; light: LightSource }[] = [];
      for (const layer of [world.tiles, ...world.upperTiles]) {
        layer.forEach((id, i) => {
          const light = world.elements.get(id)?.light;
          if (light) found.push({ x: i % world.width, y: Math.floor(i / world.width), light });
        });
      }
      this.terrainLights.set(world, lights = found);
    }
    return lights;
  }

  /** Noir tout autour d'un cercle centré sur le joueur ; le cercle rétrécit puis s'agrandit. */
  private drawIris({ closing, progress }: { closing: boolean; progress: number }, player: { x: number; y: number }, width: number, height: number): void {
    const { ctx, tileSize: S } = this;
    const cx = player.x + S / 2, cy = player.y + S / 2;
    const reach = Math.hypot(Math.max(cx, width - cx), Math.max(cy, height - cy));
    const open = closing ? 1 - easeInOut(progress) : easeInOut(progress);
    ctx.save();
    ctx.fillStyle = BACKGROUND;
    ctx.beginPath();
    ctx.rect(0, 0, width, height);
    if (open > 0) ctx.arc(cx, cy, open * reach, 0, Math.PI * 2, true);
    ctx.fill('evenodd');
    // Léger fondu au noir dans les derniers instants de la fermeture (et les premiers de l'ouverture).
    const fade = closing ? Math.max(0, progress - 0.6) / 0.4 : Math.max(0, 0.4 - progress) / 0.4;
    if (fade > 0) {
      ctx.globalAlpha = fade;
      ctx.fillRect(0, 0, width, height);
    }
    ctx.restore();
  }

  /** Case de la carte sous un point de l'écran (d'après la dernière image rendue). */
  cellAt(clientX: number, clientY: number): { x: number; y: number } {
    return {
      x: Math.floor((clientX + this.lastCamera.x) / this.tileSize),
      y: Math.floor((clientY + this.lastCamera.y) / this.tileSize),
    };
  }

  /**
   * Fait glisser le cadrage de scène vers sa cible (rien hors scène, ou en combat : la caméra suit alors le joueur ou
   * l'arène).
   */
  private frame(scene: Scene, now: number): void {
    const framing = this.framing;
    const elapsed = framing.at ? Math.min(100, now - framing.at) : 0;
    framing.at = now;
    // Nouvelle carte : la caméra y arrive sur le joueur, sans glisser depuis l'ancienne.
    if (scene.map.id !== framing.mapId) Object.assign(framing, { x: 0, y: 0, mapId: scene.map.id });
    const goal = scene.focus && !scene.battle && !scene.transition ? this.framingGoal(scene, scene.focus) : { x: 0, y: 0 };
    const k = 1 - Math.exp(-elapsed / FRAMING.ms);
    framing.x += (goal.x - framing.x) * k;
    framing.y += (goal.y - framing.y) * k;
    // Presque arrivé : il se pose (et le dessin direct, sans réduction, reprend).
    if (Math.abs(goal.x - framing.x) < 0.5 && Math.abs(goal.y - framing.y) < 0.5) Object.assign(framing, { x: goal.x, y: goal.y });
  }

  /**
   * Cible du cadrage : le décalage de la caméra (centrée sur le joueur) qui centre le personnage qui parle dans la zone
   * libre de l'écran, au-dessus de la boîte de dialogue. La caméra ne sort pas de la carte, sauf s'il le faut pour garder
   * le personnage entre les marges.
   */
  private framingGoal(scene: Scene, { entity, bottom }: { entity: Entity; bottom: number }): { x: number; y: number } {
    const S = this.tileSize;
    const side = FRAMING.side * S, above = FRAMING.above * S, below = FRAMING.below * S;
    const speaker = { x: (entity.renderX + entity.drift) * S + S / 2, y: (entity.renderY - entity.hop) * S + S / 2 };
    const free = Math.max(innerHeight / 3, Math.min(innerHeight, bottom));
    const base = this.camera(scene, innerWidth, innerHeight);
    const clamp = (value: number, low: number, high: number) => (low > high ? (low + high) / 2 : Math.max(low, Math.min(high, value)));
    // Sur un axe : centré sur le personnage, dans la carte, puis le personnage entre `near` et `far` du bord de la vue.
    const axis = (start: number, mapSize: number, view: number, center: number, near: number, far: number, key: 'x' | 'y') => {
      const centered = speaker[key] - center;
      const onMap = mapSize <= view ? (mapSize - view) / 2 : clamp(centered, 0, mapSize - view);
      return clamp(onMap, speaker[key] - far, speaker[key] - near) - start;
    };
    return {
      x: axis(base.x, scene.map.width * S, innerWidth, innerWidth / 2, side, innerWidth - side, 'x'),
      y: axis(base.y, scene.map.height * S, innerHeight, free / 2, above, free - below, 'y'),
    };
  }

  /**
   * Caméra centrée sur le joueur, ou glissant vers l'arène d'un combat (posée à la hauteur voulue de l'écran). Elle ne
   * sort pas de la carte, sauf en combat : l'arène (toujours dans la carte) reste au centre de l'écran, quitte à montrer
   * le noir au-delà d'un bord.
   */
  private camera({ map, self, battle }: Scene, width: number, height: number): { x: number; y: number } {
    const S = this.tileSize;
    const t = battle ? easeInOut(battle.amount) : 0;
    const lerp = (from: number, to: number) => from + (to - from) * t;
    const center = (mapSize: number, viewSize: number, focus: number, screen: number) => {
      const clamped = mapSize <= viewSize ? (mapSize - viewSize) / 2 : Math.max(0, Math.min(mapSize - viewSize, focus - screen));
      return battle ? lerp(clamped, focus - screen) : clamped;
    };
    const selfX = self.renderX * S + S / 2, selfY = self.renderY * S + S / 2;
    return {
      x: Math.round(center(map.width * S, width, battle ? lerp(selfX, battle.focus.x * S) : selfX, width / 2)),
      y: Math.round(center(map.height * S, height, battle ? lerp(selfY, battle.focus.y * S) : selfY, battle ? lerp(height / 2, battle.screenY) : height / 2)),
    };
  }

  /** Point de la carte (en cases) sur l'écran, d'après la dernière image rendue (avant le zoom du combat). */
  project(x: number, y: number): { x: number; y: number } {
    return { x: x * this.tileSize - this.lastCamera.x, y: y * this.tileSize - this.lastCamera.y };
  }

  /** Taille d'une case à l'écran, en pixels (avant le zoom du combat). */
  get cell(): number {
    return this.tileSize;
  }

  /** Obscurité de la carte à la dernière image (0 : plein jour). */
  get ambientDarkness(): number {
    return this.darkness;
  }

  /**
   * Cercle de combat au sol : une ellipse à la couleur du type adverse, un anneau en pointillés qui tourne lentement
   * et des repères aux quatre points cardinaux ; il se dessine en s'ouvrant avec la caméra.
   */
  private drawBattleRing({ amount, ring }: BattleScene, camera: { x: number; y: number }, now: number): void {
    if (amount <= 0) return;
    const { ctx, tileSize: S } = this;
    const open = easeInOut(Math.min(1, amount));
    const cx = ring.x * S - camera.x, cy = ring.y * S - camera.y;
    const rx = ring.rx * S * (0.6 + 0.4 * open), ry = ring.ry * S * (0.6 + 0.4 * open);
    const line = Math.max(2, Math.round(S / 16));
    ctx.save();
    ctx.globalAlpha = open;
    // Sol assombri, plus clair au centre : l'arène se détache du décor.
    const floor = ctx.createRadialGradient(cx, cy, 0, cx, cy, rx);
    floor.addColorStop(0, 'rgba(255, 255, 255, .10)');
    floor.addColorStop(0.75, 'rgba(10, 6, 20, .18)');
    floor.addColorStop(1, 'rgba(10, 6, 20, .32)');
    ctx.fillStyle = floor;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
    // Bord lumineux, qui respire.
    ctx.strokeStyle = ring.color;
    ctx.shadowColor = ring.color;
    ctx.shadowBlur = S * (0.25 + 0.1 * Math.sin(now / 400));
    ctx.lineWidth = line;
    ctx.stroke();
    ctx.shadowBlur = 0;
    // Anneau intérieur en pointillés, qui tourne.
    ctx.globalAlpha = open * 0.7;
    ctx.lineWidth = Math.max(1, line / 2);
    ctx.setLineDash([S * 0.3, S * 0.22]);
    ctx.lineDashOffset = -now / 40;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx * 0.86, ry * 0.86, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    // Repères en losange aux quatre points cardinaux.
    ctx.globalAlpha = open;
    ctx.fillStyle = ring.color;
    const mark = S * 0.14;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const mx = cx + dx * rx, my = cy + dy * ry;
      ctx.beginPath();
      ctx.moveTo(mx, my - mark);
      ctx.lineTo(mx + mark, my);
      ctx.lineTo(mx, my + mark);
      ctx.lineTo(mx - mark, my);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  /**
   * Redessine le terrain par-dessus le bas d'un personnage (ou de son monstre suiveur) quand il se tient dans des hautes herbes. La bande masquée
   * suit le sprite (et non sa case arrondie) : entre deux cases d'herbe, elle est couverte par les deux, sans que
   * le corps réapparaisse pendant le pas ; en sortant de l'herbe, seule la partie encore dans l'herbe reste cachée.
   */
  private coverInGrass(world: ElementScene, entity: { renderX: number; renderY: number }, camera: { x: number; y: number }, now: number): void {
    const { ctx, tileSize: S } = this;
    const bandTop = entity.renderY + 1 - GRASS_COVER, bandBottom = entity.renderY + 1;
    const cells: [number, number][] = [];
    for (let y = Math.floor(bandTop); y < Math.ceil(bandBottom); y++) {
      for (let x = Math.floor(entity.renderX); x < Math.ceil(entity.renderX + 1); x++) {
        if (x >= 0 && y >= 0 && x < world.width && y < world.height && world.cells.covering.has(y * world.width + x)) cells.push([x, y]);
      }
    }
    if (cells.length === 0) return;
    ctx.save();
    ctx.beginPath();
    ctx.rect(Math.round(entity.renderX * S - camera.x), Math.round(bandTop * S - camera.y), S, Math.round(S * GRASS_COVER));
    ctx.clip();
    for (const [x, y] of cells) drawTerrainCell(ctx, world, x, y, x * S - camera.x, y * S - camera.y, S, now);
    ctx.restore();
  }

  /** Lance une gerbe de brins quand un personnage arrive sur une case de hautes herbes, puis dessine celles en vol. */
  private drawGrassBursts(world: ElementScene, characters: readonly Entity[], camera: { x: number; y: number }, now: number): void {
    for (const character of characters) {
      const last = this.grassCells.get(character);
      const { x, y } = character;
      if (last?.world === world && last.x === x && last.y === y) continue;
      this.grassCells.set(character, { world, x, y });
      // Pas d'éclat à l'apparition sur la carte (arrivée, téléportation) : seulement en y marchant.
      if (last?.world !== world || Math.abs(last.x - x) + Math.abs(last.y - y) > 2) continue;
      if (x < 0 || y < 0 || x >= world.width || y >= world.height || !world.cells.covering.has(y * world.width + x)) continue;
      const colors = this.grassColor(world, x, y, now);
      if (!colors) continue;
      const blades = Array.from({ length: GRASS_BURST.count }, (_, i) => ({
        dx: (Math.random() - 0.5) * 0.6,
        vx: (Math.random() - 0.5) * 2.4,
        vy: 1.4 + Math.random() * 1.2,
        color: colors[i % 3 === 0 ? 1 : 0],
        tall: Math.random() < 0.35
      }));
      this.grassBursts.push({ world, x, y, startAt: now + GRASS_BURST.delayMs, blades });
    }
    this.grassBursts = this.grassBursts.filter((burst) => burst.world === world && now - burst.startAt < GRASS_BURST.lifeMs);
    const { ctx, tileSize: S } = this;
    // Un pixel du pixel-art agrandi.
    const pixel = Math.max(1, Math.round(S / 16));
    for (const burst of this.grassBursts) {
      const age = now - burst.startAt;
      if (age < 0) continue;
      const t = age / 1000, life = age / GRASS_BURST.lifeMs;
      ctx.save();
      ctx.globalAlpha = life < 0.6 ? 1 : 1 - (life - 0.6) / 0.4;
      for (const { dx, vx, vy, color, tall } of burst.blades) {
        const cx = burst.x + 0.5 + dx + vx * t;
        const cy = burst.y + 0.85 - (vy * t - GRASS_BURST.gravity * t * t / 2);
        ctx.fillStyle = color;
        ctx.fillRect(Math.round(cx * S - camera.x), Math.round(cy * S - camera.y), pixel, tall ? pixel * 2 : pixel);
      }
      ctx.restore();
    }
  }

  /**
   * Couleur la plus présente du terrain qui couvre la case (x, y), et une version éclaircie, mesurées une fois par
   * élément en le dessinant à sa taille d'origine ; null tant que son image n'est pas chargée.
   */
  private grassColor(world: ElementScene, x: number, y: number, now: number): [string, string] | null {
    const index = y * world.width + x;
    for (let layer = world.upperTiles.length; layer >= 0; layer--) {
      const id = (layer === 0 ? world.tiles : world.upperTiles[layer - 1])?.[index] ?? EMPTY_TILE;
      if (!world.elements.get(id)?.coversCharacters) continue;
      const cached = this.grassColors.get(id);
      if (cached) return cached;
      if (!this.grassSampler) {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = 16;
        this.grassSampler = canvas.getContext('2d', { willReadFrequently: true });
      }
      const sampler = this.grassSampler;
      if (!sampler) return null;
      sampler.clearRect(0, 0, 16, 16);
      sampler.imageSmoothingEnabled = false;
      drawTerrainCell(sampler, world, x, y, 0, 0, 16, now, layer);
      let data: Uint8ClampedArray;
      try { data = sampler.getImageData(0, 0, 16, 16).data; } catch { return null; }
      // Couleurs regroupées par paliers de 16 : le palier le plus fréquent, et la moyenne de ses pixels.
      const buckets = new Map<number, { n: number; r: number; g: number; b: number }>();
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3]! < 128) continue;
        const r = data[i]!, g = data[i + 1]!, b = data[i + 2]!;
        const key = (r >> 4) << 8 | (g >> 4) << 4 | b >> 4;
        const bucket = buckets.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
        Object.assign(bucket, { n: bucket.n + 1, r: bucket.r + r, g: bucket.g + g, b: bucket.b + b });
        buckets.set(key, bucket);
      }
      let best: { n: number; r: number; g: number; b: number } | undefined;
      for (const bucket of buckets.values()) if (!best || bucket.n > best.n) best = bucket;
      if (!best) return null;
      const [r, g, b] = [best.r, best.g, best.b].map((sum) => Math.round(sum / best.n)) as [number, number, number];
      const light = (c: number) => Math.round(c + (255 - c) * 0.3);
      const colors: [string, string] = [`rgb(${r},${g},${b})`, `rgb(${light(r)},${light(g)},${light(b)})`];
      this.grassColors.set(id, colors);
      return colors;
    }
    return null;
  }

  private resize(): void {
    // Échelle entière (×2 pour un écran à 125 % ou 175 %) : les cases tombent sur des pixels entiers et se touchent
    // sans jointure ; le navigateur réduit ensuite l'image d'un bloc. À une échelle fractionnaire, chaque case est
    // lissée sur ses bords et des lignes fines apparaissent entre elles.
    const dpr = this.economy ? 1 : Math.ceil(window.devicePixelRatio || 1);
    this.canvas.width = Math.round(innerWidth * dpr);
    this.canvas.height = Math.round(innerHeight * dpr);
    this.canvas.style.width = `${innerWidth}px`;
    this.canvas.style.height = `${innerHeight}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // Environ 18 cases en largeur (11 sur téléphone, pour rester lisible), avec une taille multiple de
    // 16 : le pixel-art du tileset est agrandi d'un facteur entier, sans jointures entre tuiles.
    const columns = innerWidth < 600 ? 11 : 18;
    const ideal = Math.min(innerWidth / columns, innerHeight / 11);
    // Le zoom ajoute ou retire 16 pixels par cran : l'agrandissement reste entier.
    const base = Math.max(32, Math.min(64, Math.round(ideal / 16) * 16));
    this.tileSize = Math.max(16, Math.min(128, base + this.zoom * 16));
    this.ctx.imageSmoothingEnabled = false;
  }

  /** Mode économie (appareils peu puissants) : 30 images par seconde, résolution de l'écran non multipliée, pas de météo. */
  setEconomy(on: boolean): void {
    if (on === this.economy) return;
    this.economy = on;
    this.resize();
  }

  setZoom(zoom: number): void {
    this.zoom = zoom;
    this.resize();
  }
}
